/**
 * Assembly Status Badge - Shows whether the item is assembled or flatpack
 */

import { Package, Wrench, PackageOpen, Puzzle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface Props {
  status: 'flatpack_sealed' | 'flatpack_opened' | 'assembled' | 'disassembled';
  size?: 'sm' | 'md';
}

const STATUS_ICONS: Record<string, typeof Package> = {
  flatpack_sealed: Package,
  flatpack_opened: PackageOpen,
  assembled: Wrench,
  disassembled: Puzzle,
};

const STATUS_COLORS: Record<string, string> = {
  flatpack_sealed: 'text-green-600',
  flatpack_opened: 'text-blue-600',
  assembled: 'text-purple-600',
  disassembled: 'text-orange-600',
};

export default function AssemblyStatusBadge({ status, size = 'sm' }: Props) {
  const { t } = useLanguage();

  const STATUS_LABELS: Record<string, string> = {
    flatpack_sealed: t('assembly.sealedFlatpack', 'Sealed Flatpack'),
    flatpack_opened: t('assembly.openedFlatpack', 'Opened Flatpack'),
    assembled: t('assembly.assembled', 'Assembled'),
    disassembled: t('assembly.disassembled', 'Disassembled'),
  };

  const Icon = STATUS_ICONS[status] || STATUS_ICONS.flatpack_sealed;
  const color = STATUS_COLORS[status] || STATUS_COLORS.flatpack_sealed;
  const label = STATUS_LABELS[status] || STATUS_LABELS.flatpack_sealed;

  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <span className={`inline-flex items-center gap-1 ${color} ${textSize}`}>
      <Icon className={iconSize} />
      <span>{label}</span>
    </span>
  );
}
